import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import './AdminFinanceReports.css';

function AdminFinanceReports() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('day');

  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ['finance-sales', dateFrom, dateTo, groupBy],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('group_by', groupBy);
      const res = await apiClient.get(`/admin/finance-reports/sales?${params}`);
      return res.data;
    },
  });

  const { data: marginData, isLoading: marginLoading } = useQuery({
    queryKey: ['finance-margin', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/finance-reports/margin?${params}`);
      return res.data;
    },
  });

  const { data: discountsData, isLoading: discountsLoading } = useQuery({
    queryKey: ['finance-discounts', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/finance-reports/discounts?${params}`);
      return res.data;
    },
  });

  const { data: refundsData, isLoading: refundsLoading } = useQuery({
    queryKey: ['finance-refunds', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/finance-reports/refunds?${params}`);
      return res.data;
    },
  });

  const { data: abandonedData, isLoading: abandonedLoading } = useQuery({
    queryKey: ['finance-abandoned', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await apiClient.get(`/admin/finance-reports/abandoned-carts?${params}`);
      return res.data;
    },
  });

  if (salesLoading || marginLoading || discountsLoading || refundsLoading || abandonedLoading) {
    return <div className="admin-finance-reports-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-finance-reports-page container">
      <div className="reports-header">
        <h1>Finansinės ataskaitos</h1>
      </div>

      {/* Datų filtrai */}
      <div className="filters-section">
        <div className="date-filters">
          <div className="filter-group">
            <label>Nuo datos:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>Iki datos:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>Grupavimas:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="date-input"
            >
              <option value="day">Pagal dieną</option>
              <option value="month">Pagal mėnesį</option>
              <option value="year">Pagal metus</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pardavimų ataskaita */}
      <div className="report-section">
        <h2>Pardavimai</h2>
        {salesData?.sales && salesData.sales.length > 0 ? (
          <div className="sales-table-wrapper">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Periodas</th>
                  <th>Užsakymų</th>
                  <th>Unikalių klientų</th>
                  <th>Prekių suma</th>
                  <th>PVM</th>
                  <th>Nuolaidos</th>
                  <th>Pristatymas</th>
                  <th>Bendra suma</th>
                  <th>Vid. užsakymo vertė</th>
                </tr>
              </thead>
              <tbody>
                {salesData.sales.map((sale, index) => (
                  <tr key={index}>
                    <td>{sale.period}</td>
                    <td>{sale.order_count}</td>
                    <td>{sale.unique_customers}</td>
                    <td>{parseFloat(sale.total_subtotal || 0).toFixed(2)} €</td>
                    <td>{parseFloat(sale.total_tax || 0).toFixed(2)} €</td>
                    <td>{parseFloat(sale.total_discount || 0).toFixed(2)} €</td>
                    <td>{parseFloat(sale.total_shipping || 0).toFixed(2)} €</td>
                    <td><strong>{parseFloat(sale.total_revenue || 0).toFixed(2)} €</strong></td>
                    <td>{parseFloat(sale.avg_order_value || 0).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-message">Nėra duomenų</p>
        )}
      </div>

      {/* Statistika */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Marža</h3>
          <div className="stat-content">
            <p className="stat-value">{parseFloat(marginData?.total_margin || 0).toFixed(2)} €</p>
            <p className="stat-label">Bendra marža</p>
            <p className="stat-detail">Maržos %: {parseFloat(marginData?.margin_percentage || 0).toFixed(2)}%</p>
            <p className="stat-detail">Vid. marža: {parseFloat(marginData?.avg_margin || 0).toFixed(2)} €</p>
          </div>
        </div>

        <div className="stat-card">
          <h3>Nuolaidos</h3>
          <div className="stat-content">
            <p className="stat-value">{parseFloat(discountsData?.total_discount_amount || 0).toFixed(2)} €</p>
            <p className="stat-label">Bendra nuolaidų suma</p>
            <p className="stat-detail">Užsakymų su nuolaida: {discountsData?.orders_with_discount || 0}</p>
            <p className="stat-detail">Vid. nuolaida: {parseFloat(discountsData?.avg_discount || 0).toFixed(2)} €</p>
          </div>
        </div>

        <div className="stat-card">
          <h3>Grąžinimai</h3>
          <div className="stat-content">
            <p className="stat-value">{parseFloat(refundsData?.total_refund_amount || 0).toFixed(2)} €</p>
            <p className="stat-label">Bendra grąžinimų suma</p>
            <p className="stat-detail">Grąžinimų skaičius: {refundsData?.refund_count || 0}</p>
            <p className="stat-detail">Vid. grąžinimas: {parseFloat(refundsData?.avg_refund || 0).toFixed(2)} €</p>
          </div>
        </div>

        <div className="stat-card">
          <h3>Apleisti krepšeliai</h3>
          <div className="stat-content">
            <p className="stat-value">{parseFloat(abandonedData?.total_abandoned_value || 0).toFixed(2)} €</p>
            <p className="stat-label">Bendra apleistų krepšelių vertė</p>
            <p className="stat-detail">Apleisti: {abandonedData?.total_abandoned || 0}</p>
            <p className="stat-detail">Atkurti: {abandonedData?.recovered_count || 0} ({parseFloat(abandonedData?.recovery_rate || 0).toFixed(1)}%)</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminFinanceReports;
